import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { Box, Stack, Typography } from "@mui/material";
import {
  getLicenseTransactionTypeLabel,
  LICENSE_TRANSACTION_TYPE,
  normalizeLicenseTransactionType,
  ORG_TRANSACTION_TYPE_OPTIONS,
} from "const/license";

const columnHelper = createColumnHelper();

export const defaultTabs = [
  { name: "All", value: "all", isActive: true, tabCount: 0 },
  ...ORG_TRANSACTION_TYPE_OPTIONS.map((type) => ({
    ...type,
    isActive: false,
    tabCount: 0,
  })),
];

export const formatPeriod = (period) => {
  if (!period) return undefined;
  if (Array.isArray(period)) {
    return period.length > 1
      ? `${period[0]} - ${period[period.length - 1]}`
      : period[0];
  }
  return period;
};

export const maskLicenseKey = (key) => {
  if (!key) return "-";
  const parts = String(key).split("-");
  if (parts.length < 3) return "••••••";
  return parts
    .map((part, index) =>
      index === 0 || index === parts.length - 1 ? part : "•••••",
    )
    .join("-");
};

export const getDescriptionItems = (tx) => {
  const type = normalizeLicenseTransactionType(tx.type);
  const p = tx.descriptionParams || {};

  const getParam = (key) => {
    const lKey = key.toLowerCase();
    const actualKey = Object.keys(p).find((k) => k.toLowerCase() === lKey);
    return actualKey ? p[actualKey] : undefined;
  };

  let rows = [];

  switch (type) {
    case LICENSE_TRANSACTION_TYPE.IMPORT:
      rows = [
        ["Actor", getParam("account")],
        ["License Key", maskLicenseKey(getParam("key"))],
        ["Txn ID", getParam("txnId")],
      ];
      break;
    case LICENSE_TRANSACTION_TYPE.SUBSCRIBE:
    case LICENSE_TRANSACTION_TYPE.MONTHLY_CHARGE:
    case LICENSE_TRANSACTION_TYPE.ADDED_DEVICE:
    case LICENSE_TRANSACTION_TYPE.REMOVE_DEVICE:
    case LICENSE_TRANSACTION_TYPE.UNSUBSCRIBE: {
      const days = getParam("days");
      const returned =
        [
          LICENSE_TRANSACTION_TYPE.REMOVE_DEVICE,
          LICENSE_TRANSACTION_TYPE.UNSUBSCRIBE,
        ].includes(type) && days !== undefined
          ? `${days} (unused, returned)`
          : days;

      const rawDevice = getParam("device") ?? getParam("devices");
      const totalDevice = getParam("totalDevice") ?? getParam("totalDevices");
      let deviceValue = rawDevice;

      if (rawDevice !== undefined && rawDevice !== null && rawDevice !== "") {
        if (type === LICENSE_TRANSACTION_TYPE.ADDED_DEVICE) {
          const num = Number(rawDevice);
          const countStr = !isNaN(num)
            ? `+${Math.abs(num)}`
            : String(rawDevice).startsWith("+")
              ? rawDevice
              : `+${rawDevice}`;
          deviceValue =
            totalDevice !== undefined && totalDevice !== null && totalDevice !== ""
              ? `${countStr} (site total ${totalDevice})`
              : countStr;
        } else if (type === LICENSE_TRANSACTION_TYPE.REMOVE_DEVICE) {
          const num = Number(rawDevice);
          const countStr = !isNaN(num)
            ? `−${Math.abs(num)}`
            : String(rawDevice).startsWith("-") || String(rawDevice).startsWith("−")
              ? `−${String(rawDevice).replace(/^[-−]/, "")}`
              : `−${rawDevice}`;
          deviceValue =
            totalDevice !== undefined && totalDevice !== null && totalDevice !== ""
              ? `${countStr} (site total ${totalDevice})`
              : countStr;
        }
      }

      rows = [
        ["Site", getParam("site"), false],
        [
          "Scope",
          getParam("site")
            ? undefined
            : "MSP layer — no site, no device count",
          false,
        ],
        ["Service", getParam("service")],
        ["Device", deviceValue, false],
        ["Rate", getParam("rate")],
        ["Days", returned ?? getParam("daysGranted")],
        ["Period", formatPeriod(getParam("period"))],
        ["Actor", getParam("account")],
        ["Txn ID", getParam("txnId")],
      ];
      break;
    }
    case LICENSE_TRANSACTION_TYPE.PURCHASE:
      rows = [
        ["Actor", getParam("account")],
        ["Order Ref.", getParam("orderReference")],
        ["Granted Credits", getParam("grantedCredits")],
        ["Txn ID", getParam("txnId")],
      ];
      break;
    case LICENSE_TRANSACTION_TYPE.REVERSAL:
      rows = [
        ["Order Ref.", getParam("orderReference")],
        ["Original Grant", getParam("originalGrant")],
        ["This Reversal", getParam("thisReversal")],
        ["Cumulative Reversed", getParam("cumulativeReversed")],
        ["Reclaimed in Full", getParam("reclaimedInFull")],
        ["Txn ID", getParam("txnId")],
      ];
      break;
    case LICENSE_TRANSACTION_TYPE.COVER_CURRENT_MONTH:
      rows = [
        ["Site", getParam("site"), false],
        [
          "Scope",
          getParam("site")
            ? undefined
            : "MSP layer — no site, no device count",
          false,
        ],
        ["Service", getParam("service")],
        ["Device", getParam("device"), false],
        ["Rate", getParam("rate")],
        ["Days", getParam("days")],
        ["Period", formatPeriod(getParam("period"))],
        ["Settles", getParam("settleTxnId")],
        ["Actor", getParam("account")],
        ["Txn ID", getParam("txnId")],
      ];
      break;
    case LICENSE_TRANSACTION_TYPE.PARTIAL_GRANT: {
      const daysGranted = getParam("daysGranted");
      const daysTotal = getParam("daysTotal");
      const daysGrantedValue =
        daysGranted !== undefined && daysTotal !== undefined
          ? `${daysGranted} of ${daysTotal}`
          : daysGranted ?? (daysTotal ? `- of ${daysTotal}` : undefined);

      rows = [
        ["Site", getParam("site"), false],
        [
          "Scope",
          getParam("site")
            ? undefined
            : "MSP layer — no site, no device count",
          false,
        ],
        ["Service", getParam("service")],
        ["Device", getParam("device"), false],
        ["Rate", getParam("rate")],
        ["Days granted", daysGrantedValue],
        ["Days Unbilled", getParam("daysUnbilled")],
        ["Period", formatPeriod(getParam("period"))],
        ["Actor", getParam("account")],
        ["Txn ID", getParam("txnId")],
      ];
      break;
    }
    case LICENSE_TRANSACTION_TYPE.MSP_DISBURSEMENT:
    case LICENSE_TRANSACTION_TYPE.MSP_WITHDRAWAL: {
      const fromMsp = getParam("fromMsp");
      const fromOrg = getParam("fromOrg");
      const toMsp = getParam("toMsp");
      const toOrg = getParam("toOrg");

      const formatWallet = (val, suffix) => {
        if (!val) return undefined;
        return val.includes(suffix) ? val : `${val} ${suffix}`;
      };

      rows = [
        ["From", formatWallet(fromMsp, "(MSP Wallet)"), false],
        ["From", formatWallet(fromOrg, "(Org Wallet)"), false],
        ["To", formatWallet(toMsp, "(MSP Wallet)"), false],
        ["To", formatWallet(toOrg, "(Org Wallet)"), false],
        ["Actor", getParam("account")],
        ["Txn ID", getParam("txnId")],
      ];
      break;
    }
    case LICENSE_TRANSACTION_TYPE.COMPENSATORY_KEY_ISSUED:
      rows = [
        ["License Key", maskLicenseKey(getParam("key"))],
        ["Key Value", getParam("keyValue")],
        ["Sent To", getParam("sentTo")],
        ["Reason", getParam("reason")],
        ["Txn ID", getParam("txnId")],
      ];
      break;
    case LICENSE_TRANSACTION_TYPE.MANUAL_ADJUSTMENT:
      rows = [
        ["Operator", getParam("operator")],
        ["Approved By", getParam("approvedBy")],
        ["Reason", getParam("reason")],
        ["Txn ID", getParam("txnId")],
      ];
      break;
    default:
      return [];
  }

  return rows
    .filter(
      ([, value, isRequired = true]) =>
        isRequired || (value !== null && value !== undefined && value !== ""),
    )
    .map(([label, value]) => ({ label, value: value ?? "-" }));
};

export const getDescriptionText = (tx, separator = " | ") => {
  const items = getDescriptionItems(tx);
  if (!items || items.length === 0) {
    return tx.description || "-";
  }
  return items.map(({ label, value }) => `${label}: ${value}`).join(separator);
};

export const getDescription = (tx) => {
  const items = getDescriptionItems(tx);
  if (!items || items.length === 0) return "-";

  return (
    <Stack spacing={0}>
      {items.map(({ label, value }) => (
        <Box key={label}>
          <Typography
            variant='body2'
            component='span'
            sx={{ color: "text.secondary", fontSize: "13px" }}
          >
            {label}:{" "}
          </Typography>
          <Typography
            variant='body2'
            component='span'
            sx={{ fontWeight: "bold", fontSize: "13px" }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

export const getTransactionsTableColumns = ({ theme }) => [
  columnHelper.accessor(
    (row) => dayjs.unix(row.timestamp).format("YYYY-MM-DD HH:mm:ss"),
    {
      id: "formattedDate",
      header: "Date",
      sortingFn: (rowA, rowB) => {
        return rowA.original.timestamp - rowB.original.timestamp;
      },
      cell: (info) => (
        <Typography sx={{ fontSize: "13px", color: "#111" }}>
          {info.getValue()}
        </Typography>
      ),
    },
  ),
  columnHelper.accessor("type", {
    header: "Type",
    cell: (info) => (
      <Typography sx={{ fontSize: "13px", color: "#111" }}>
        {getLicenseTransactionTypeLabel(info.getValue())}
      </Typography>
    ),
  }),
  columnHelper.accessor("change", {
    header: "Amount",
    sortingFn: (rowA, rowB, columnId) => {
      const a = Number(rowA.getValue(columnId)) || 0;
      const b = Number(rowB.getValue(columnId)) || 0;
      return a - b;
    },
    cell: (info) => {
      const value = info.getValue();
      const isPositive = value > 0;
      return (
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "13px",
            color: isPositive ? "#2E7D32" : "#000000",
          }}
        >
          {isPositive ? `+${value.toLocaleString()}` : value.toLocaleString()}
        </Typography>
      );
    },
  }),
  columnHelper.accessor(
    (row) => {
      const type = normalizeLicenseTransactionType(row.type);
      const p = row.descriptionParams || {};
      const getParam = (key) => {
        const lKey = key.toLowerCase();
        const actualKey = Object.keys(p).find((k) => k.toLowerCase() === lKey);
        return actualKey ? p[actualKey] : undefined;
      };
      if (type === LICENSE_TRANSACTION_TYPE.IMPORT) {
        return `Account: ${getParam("account") || ""} License Key: ${getParam("key") || ""}`;
      }
      return `Site: ${getParam("site") || ""} ${getParam("devices") || getParam("device") || ""} Days: ${getParam("days") || ""} Service: ${getParam("service") || ""}`;
    },
    {
      id: "description",
      header: "Description",
      cell: (info) => getDescription(info.row.original),
    },
  ),
  columnHelper.accessor("balance", {
    header: "Balance After",
    sortingFn: (rowA, rowB, columnId) => {
      const a = Number(rowA.getValue(columnId)) || 0;
      const b = Number(rowB.getValue(columnId)) || 0;
      return a - b;
    },
    cell: (info) => (
      <Typography sx={{ fontSize: "13px", color: "#111" }}>
        {info.getValue()?.toLocaleString()}
      </Typography>
    ),
  }),
];
